import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Table,
  TableRow,
  TableCell,
  TextLink,
} from '@spotify-internal/encore-creator-web';
import {
  black,
  spacer12,
  spacer24,
  screenSmMin,
} from '@spotify-internal/encore-foundation';
import { sendEvent } from '@mrkt/features/Platform/googleAnalytics';
import { applyMetricSuffix } from '../../../../shared/lib/numberHelpers';
import StatsTopHeader from '../StatsTopHeader';
import { StatsDelta } from '../StatsDelta';
import { StatsDeltaTitle } from '../Style';

const OverviewRow = styled(TableRow)`
  display: flex;
  justify-content: space-between;
  padding-bottom: ${spacer12};
  padding-top: ${spacer12};

  &:hover {
    cursor: pointer;
  }
`;

const OverviewCell = styled(TableCell)`
  align-items: center;
  display: flex;
  padding-left: 0;
  padding-right: 0;
`;

const OverviewTable = styled(Table)`
  border-top: 1px solid ${black};
  padding-bottom: ${spacer24};
`;

const OverviewArea = styled.div`
  @media (min-width: ${screenSmMin}) {
    padding-bottom: ${spacer24};
  }
`;

const StyledTextLink = styled(TextLink)`
  &&& {
    text-decoration: none;
    &:hover {
      text-decoration: none;
      color: inherit;
    }
  }
`;

const Overview = React.memo(props => {
  const { listeners, streams, followers } = props;

  const OverviewData = [
    {
      id: 'listeners',
      label: 'Listeners',
      data: listeners,
    },
    {
      id: 'streams',
      label: 'Streams',
      data: streams,
    },
    {
      id: 'followers',
      label: 'Followers',
      data: followers,
    },
  ];

  return (
    <OverviewArea>
      {props.isMobile ? <StatsTopHeader latestDate={props.latestDate} /> : null}
      <OverviewTable data-testid="overview-table">
        <tbody>
          {OverviewData.map(row => (
            <OverviewRow
              data-testid="overview-row"
              hover
              key={row.id}
              onClick={() => {
                sendEvent({
                  eventCategory: 'Navigate',
                  eventAction: 'audience',
                  eventLabel: `${row.id}`,
                });

                props.history.push(
                  `/artist/${props.artist.id}/audience?audience-filter=${row.id}&time-filter=7day`,
                );
              }}
            >
              <OverviewCell highlight>
                <StyledTextLink href="#">{row.label}</StyledTextLink>
              </OverviewCell>
              <OverviewCell align="right">
                <StatsDeltaTitle>
                  {row.data && row.data.total !== undefined
                    ? applyMetricSuffix(row.data.total)
                    : '-'}
                </StatsDeltaTitle>
                {!!row.data ? (
                  <StatsDelta
                    delta={
                      row.id === 'followers'
                        ? row.data.delta
                        : row.data.deltaPct
                    }
                    isDeltaPct={row.id !== 'followers'}
                    id={row.id}
                  />
                ) : null}
              </OverviewCell>
            </OverviewRow>
          ))}
        </tbody>
      </OverviewTable>
    </OverviewArea>
  );
});

Overview.propTypes = {
  artist: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  isMobile: PropTypes.bool.isRequired,
  latestDate: PropTypes.string,
  listeners: PropTypes.shape({
    total: PropTypes.number,
    deltaPct: PropTypes.number,
  }).isRequired,
  streams: PropTypes.shape({
    total: PropTypes.number,
    deltaPct: PropTypes.number,
  }).isRequired,
  followers: PropTypes.shape({
    delta: PropTypes.number,
    total: PropTypes.number,
    deltaPct: PropTypes.number,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
};

/* eslint-disable-next-line import/no-default-export */
export default Overview;
